import React, { useRef, useMemo, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { textures } from "../lib/textures";
import { SHADOW } from "../lib/eclipse";

const vertex = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vNV;
  void main() {
    vUv = uv;
    vNV = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragment = /* glsl */ `
  uniform sampler2D uMap;
  uniform float uHasMap;
  uniform vec2 uDir2;
  uniform float uSep;
  uniform float uUmbraR;
  uniform float uPenR;
  uniform vec3 uCopper;
  uniform float uDanjonLum;
  uniform float uCoverage;
  uniform float uExtraLight;
  varying vec2 vUv;
  varying vec3 vNV;

  void main() {
    // natural geological grey base
    vec3 base = uHasMap > 0.5 ? texture2D(uMap, vUv).rgb * 1.25 : vec3(0.74, 0.73, 0.7);
    float lit = 1.35 + uExtraLight;
    vec3 col = base * lit;

    // umbra circle sweeping across the camera-facing disc
    vec2 disc = vNV.xy;
    vec2 center = uDir2 * uSep;
    float d = distance(disc, center);
    float inUmbra = 1.0 - smoothstep(uUmbraR - 0.06, uUmbraR + 0.06, d);
    float inPen = 1.0 - smoothstep(uPenR - 0.05, uPenR + 0.12, d);

    // penumbral soft grey dimming (subtle)
    col = mix(col, col * 0.5, clamp(inPen - inUmbra, 0.0, 1.0) * 0.7);

    // Blood-red transition: MOST intense at the deepest part of the shadow.
    // As total coverage rises the whole umbral disc reddens (true blood moon),
    // fading back toward natural grey only for shallow / partial eclipses.
    float t = clamp(d / uUmbraR, 0.0, 1.0);            // 0 = deepest, 1 = edge
    float depth = 1.0 - smoothstep(0.0, 1.0, t);        // deep -> 1
    vec3 bloodTint = uCopper * (1.6 + uDanjonLum * 2.0);
    float edgeRed = 0.3 + 0.65 * uCoverage;             // edges redden at high coverage
    vec3 tint = mix(mix(vec3(1.0), bloodTint, edgeRed), bloodTint, depth);
    vec3 umbraCol = base * tint * (0.32 + 0.85 * base.r);
    col = mix(col, umbraCol, inUmbra);

    gl_FragColor = vec4(col, 1.0);
  }
`;

export default function MoonBody({ simRef, radius = 1, extraLight = 0, spin = true, controlsRef }) {
  const mesh = useRef();
  const matRef = useRef();
  const vel = useRef(0);
  const dragging = useRef(false);
  const lastX = useRef(0);
  const lastDx = useRef(0);
  const { gl } = useThree();

  const uniforms = useMemo(
    () => ({
      uMap: { value: textures.moon },
      uHasMap: { value: textures.moon ? 1 : 0 },
      uDir2: { value: new THREE.Vector2(-0.5, 0.86).normalize() },
      uSep: { value: 3.0 },
      uUmbraR: { value: SHADOW.umbra },
      uPenR: { value: SHADOW.penumbra },
      uCopper: { value: new THREE.Color(0.8, 0.3, 0.12) },
      uDanjonLum: { value: 0.1 },
      uCoverage: { value: 0 },
      uExtraLight: { value: extraLight },
    }),
    [extraLight]
  );

  useEffect(() => {
    if (textures.moon) { uniforms.uMap.value = textures.moon; uniforms.uHasMap.value = 1; }
  }, [uniforms]);

  useEffect(() => {
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      vel.current = lastDx.current * 5;
      if (controlsRef?.current) controlsRef.current.enabled = true;
      gl.domElement.style.cursor = "grab";
    };
    const move = (e) => {
      if (!dragging.current || !mesh.current) return;
      const dx = (e.clientX - lastX.current) * 0.01;
      mesh.current.rotation.y += dx;
      lastDx.current = dx;
      lastX.current = e.clientX;
    };
    window.addEventListener("pointerup", up);
    window.addEventListener("pointermove", move);
    return () => { window.removeEventListener("pointerup", up); window.removeEventListener("pointermove", move); };
  }, [gl, controlsRef]);

  useFrame((_, dt) => {
    const s = simRef.current;
    const mat = matRef.current;
    if (mat && s) {
      mat.uniforms.uSep.value = s.umbraCenter.length();
      mat.uniforms.uDanjonLum.value = s.danjonLum;
      mat.uniforms.uCoverage.value = s.coverage;
      mat.uniforms.uCopper.value.setRGB(s.danjonRgb[0], s.danjonRgb[1], s.danjonRgb[2]);
    }
    if (mesh.current && !dragging.current) {
      const base = spin ? 0.06 : 0.008; // visible autonomous lunar spin
      mesh.current.rotation.y += (base + vel.current) * dt;
      vel.current *= Math.pow(0.88, dt * 60);
    }
  });

  const onDown = (e) => {
    e.stopPropagation();
    dragging.current = true;
    lastX.current = e.clientX;
    lastDx.current = 0;
    if (controlsRef?.current) controlsRef.current.enabled = false;
    gl.domElement.style.cursor = "grabbing";
  };

  return (
    <mesh ref={mesh} onPointerDown={onDown}>
      <sphereGeometry args={[radius, 48, 48]} />
      <shaderMaterial ref={matRef} vertexShader={vertex} fragmentShader={fragment} uniforms={uniforms} />
    </mesh>
  );
}
